import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input, Table, DatePicker, message, Button, ConfigProvider, Spin } from 'antd';
import { ref, get, remove } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import { MagnifyingGlass, Trash } from '@phosphor-icons/react';
import moment from 'moment';
import 'moment/locale/pl';
import plPL from 'antd/es/locale/pl_PL';
import './Wydatki.css';

const { RangePicker } = DatePicker;

const Wydatki = () => {
    const { currentFirebase } = useFirebase();
    const db = currentFirebase.database;

    const [searchValue, setSearchValue] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [wydatekData, setWydatekData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadAndDisplayData();
    }, [db]);

    const loadAndDisplayData = async () => {
        setLoading(true);
        try {
            const snapshot = await get(ref(db, '/drivers'));
            const drivers = snapshot.val();
            let wydatek = [];

            for (let driverId in drivers) {
                const driverData = drivers[driverId];
                if (driverData.invoices) {
                    for (let invoiceId in driverData.invoices) {
                        const invoice = driverData.invoices[invoiceId];
                        if (invoice.type !== 'wydatek') continue;
                        wydatek.push({
                            ...invoice,
                            driverId,
                            invoiceId,
                            purchaseDate: invoice.purchaseDate,
                        });
                    }
                }
            }

            wydatek.sort((a, b) => (b.purchaseDate || '').localeCompare(a.purchaseDate || ''));
            setWydatekData(wydatek);
            setCurrentPage(1);
        } catch (error) {
            console.error('Error fetching wydatek data:', error);
            message.error('Błąd podczas pobierania danych.');
        }
        setLoading(false);
    };

    const filteredData = useMemo(() => {
        const q = searchValue.toLowerCase();
        return wydatekData.filter(w => {
            const matchesSearch = !q || Object.values(w).some(v => v && v.toString().toLowerCase().includes(q));
            let matchesDate = true;
            if (dateRange && dateRange[0] && dateRange[1]) {
                const start = dateRange[0].format('YYYY-MM-DD');
                const end = dateRange[1].format('YYYY-MM-DD');
                matchesDate = w.purchaseDate >= start && w.purchaseDate <= end;
            }
            return matchesSearch && matchesDate;
        });
    }, [wydatekData, searchValue, dateRange]);

    const handleDelete = useCallback(async (invoiceId, driverId) => {
        setLoading(true);
        try {
            await remove(ref(db, `/drivers/${driverId}/invoices/${invoiceId}`));
            setWydatekData(prev => prev.filter(w => w.invoiceId !== invoiceId));
            message.success('Wydatek usunięty.');
        } catch (error) {
            console.error('Error deleting:', error);
            message.error('Błąd podczas usuwania.');
        }
        setLoading(false);
    }, [db]);

    const columns = useMemo(() => [
        {
            title: 'Kierowca',
            dataIndex: 'driverId',
            key: 'driverId',
            sorter: (a, b) => (a.driverId || '').localeCompare(b.driverId || ''),
            width: 140,
            render: (id) => <span className="wyd-mono">{id}</span>,
        },
        {
            title: 'Numer faktury',
            dataIndex: 'numerfaktury',
            key: 'numerfaktury',
            sorter: (a, b) => (a.numerfaktury || '').localeCompare(b.numerfaktury || ''),
            width: 180,
            render: (v) => <span className="wyd-mono">{v || '-'}</span>,
        },
        {
            title: 'Data',
            dataIndex: 'purchaseDate',
            key: 'purchaseDate',
            sorter: (a, b) => (a.purchaseDate || '').localeCompare(b.purchaseDate || ''),
            width: 110,
            render: (d) => <span style={{ fontSize: 12, color: '#6b7280' }}>{d || '-'}</span>,
        },
        {
            title: 'Brutto',
            dataIndex: 'grossAmount',
            key: 'grossAmount',
            sorter: (a, b) => parseFloat(a.grossAmount || 0) - parseFloat(b.grossAmount || 0),
            width: 110,
            align: 'right',
            render: (v) => <span className="wyd-amount">{v || '-'}</span>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (s) => {
                const cls = s === 'zaakceptowany' ? 'wyd-status-ok' : 'wyd-status-default';
                return <span className={`wyd-status ${cls}`}>{s || '-'}</span>;
            },
        },
        {
            title: '',
            key: 'action',
            width: 60,
            align: 'center',
            render: (_, record) => (
                <button className="wyd-del-btn" onClick={() => handleDelete(record.invoiceId, record.driverId)} title="Usuń">
                    <Trash size={15} />
                </button>
            ),
        },
    ], [handleDelete]);

    return (
        <ConfigProvider locale={plPL}>
            <div>
                <div className="wyd-toolbar">
                    <Input
                        prefix={<MagnifyingGlass size={16} color="#9ca3af" />}
                        placeholder="Szukaj..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        allowClear
                        style={{ flex: 2, minWidth: 150 }}
                    />
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => { setDateRange(dates || null); setCurrentPage(1); }}
                        format="YYYY-MM-DD"
                        placeholder={['Od', 'Do']}
                        style={{ flex: 1, minWidth: 200 }}
                    />
                </div>

                <Table
                    className="wyd-table"
                    columns={columns}
                    dataSource={filteredData}
                    pagination={{
                        current: currentPage,
                        pageSize: rowsPerPage,
                        total: filteredData.length,
                        onChange: (p) => setCurrentPage(p),
                        onShowSizeChange: (_, s) => { setRowsPerPage(s); setCurrentPage(1); },
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '25', '50'],
                        showTotal: (total, range) => `${range[0]}–${range[1]} z ${total}`,
                        size: 'small',
                    }}
                    rowKey="invoiceId"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    size="middle"
                />
            </div>
        </ConfigProvider>
    );
};

export default Wydatki;
